import {
    faAddressBook,
    faArrowUp,
    faCalendarAlt,
    faFileInvoiceDollar,
    faQuestionCircle,
    faSearch,
    faTicketAlt,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Spinner, StyledInput } from '@ui';
import ClubMemberPreview from 'components/ClubMemberManagement/ClubMember/ClubMemberPreview';
import { EventForm } from 'components/EventCalendar/Event/EventForm';
import EventPreview from 'components/Events/EventPreview';
import FormDataPreview from 'components/Formular/FormDataPreview';
import AllocationForm from 'components/SpaceManager/AllocationForm';
import AllocationPreview from 'components/SpaceManager/Views/TableView/AllocationPreview';
import { InvoiceForm } from 'components/UserInvoice/InvoiceForm/InvoiceForm';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import ClubMemberForm from '~/components/ClubMemberManagement/ClubMember/ClubMemberForm';
import { useDebouncedValue } from '~/hooks/useDebouncedValue';
import useModalManager from '~/hooks/useModalManager';

type GlobalSearchResult = {
    id: number | string;
    title: string;
    subtitle?: string | null;
    type: string;
    url?: string | null;
    club_id?: number | null;
};

interface Props {
    clubSlug: string;
}

const normalizeType = (type: string) => {
    const normalizedType = type?.toLowerCase();
    if (normalizedType.includes('event')) return 'event';
    if (normalizedType.includes('booking') || normalizedType === 'platzbuchung') return 'booking';
    if (normalizedType.includes('member') || normalizedType === 'mitglied') return 'member';
    if (normalizedType.includes('invoice') || normalizedType === 'rechnung') return 'invoice';
    if (normalizedType.includes('booker') || normalizedType === 'buchende') return 'booker';
    return 'other';
};

const getTypeConfig = (type: string) => {
    const normalized = normalizeType(type);
    switch (normalized) {
        case 'event':
            return { label: 'Events', icon: faCalendarAlt };
        case 'booking':
            return { label: 'Buchungen', icon: faTicketAlt };
        case 'member':
            return { label: 'Mitglieder', icon: faUser };

        case 'invoice':
            return {
                label: 'Rechnungen',
                icon: faFileInvoiceDollar
            };
        case 'booker':
            return { label: 'Buchende', icon: faAddressBook };
        default:
            return { label: 'Sonstiges', icon: faQuestionCircle };
    }
};

const toResultList = (data: any): GlobalSearchResult[] => {
    if (Array.isArray(data?.data?.data)) return data.data.data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
};

const canPreviewResult = (item: GlobalSearchResult) => {
    const type = normalizeType(item.type);
    if (type === 'invoice') return Boolean(item.url);
    if (type === 'event') return false;
    return true;
};

const BookingEditor = ({ bookingId, onClose }: { bookingId: number; onClose: () => void }) => {
    return <AllocationForm allocation={{ id: bookingId }} callback={() => onClose()} />;
};

const EventPreviewContent = ({ eventId }: { eventId: number }) => {
    const { data, isLoading } = useSWR<{ data: any }>(eventId ? `event/${eventId}` : null);
    const event = data?.data;
    if (isLoading)
        return (
            <div className="tw-p-4 tw-text-center">
                <Spinner size="sm" />
            </div>
        );

    if (!event) return <div className="tw-p-4 tw-text-accents-5">Keine Vorschau verfügbar.</div>;

    return <EventPreview eventSlug={event.eventSlug} event={event} />;
};

const removeTags = (str?: string | null) => (str ? String(str).replace(/(<([^>]+)>)/gi, '') : '');

const BookerPreviewContent = ({ bookerId }: { bookerId: number }) => {
    const { data, isLoading } = useSWR(bookerId ? `booking/${bookerId}` : null);
    const formDataId = data?.data?.form_data_id ?? bookerId;
    if (isLoading)
        return (
            <div className="tw-p-4 tw-text-center">
                <Spinner size="sm" />
            </div>
        );
    if (!formDataId) return <div className="tw-p-4 tw-text-accents-5">Keine Vorschau verfügbar.</div>;
    return <FormDataPreview formDataId={formDataId} />;
};

const ClubMemberEditContent = ({ memberId, clubSlug }: { memberId: number; clubSlug: string }) => {
    const { data, isLoading } = useSWR<{ data: any }>(memberId ? `club-member/${memberId}` : null);
    const member = data?.data;

    if (isLoading)
        return (
            <div className="tw-p-4 tw-text-center">
                <Spinner size="sm" />
            </div>
        );

    if (!member) return <div className="tw-p-4 tw-text-accents-5">Mitglied konnte nicht geladen werden.</div>;

    return <ClubMemberForm member={member} clubSlug={clubSlug} />;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderHighlightedText = (text: string, query: string) => {
    const q = query?.trim();
    if (!q || q.length < 2) return text;
    const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'ig'));
    return parts.map((part, idx) =>
        part.toLowerCase() === q.toLowerCase() ? (
            <mark key={`${part}_${idx}`} className="tw-rounded tw-bg-success/30 tw-px-[1px] tw-text-inherit">
                {part}
            </mark>
        ) : (
            part
        )
    );
};

const openInNewTab = (url?: string | null) => {
    if (!url) return;
    const popup = window.open(url, '_blank');
    if (popup != null) popup.focus();
};

export const GlobalSearch = ({ clubSlug }: Props) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState<string>('all');
    const { addModal, removeModal } = useModalManager();
    const query = useDebouncedValue(searchTerm.trim(), 250);
    const searchKey = query.length >= 2 ? [`global-search/${clubSlug}`, { query, limit: 60 }] : null;
    const { data, isLoading } = useSWR(searchKey);
    const results = useMemo(() => toResultList(data), [data]);
    const groupedResults = useMemo(() => {
        const groups: Record<string, GlobalSearchResult[]> = {};
        results.forEach((item) => {
            const type = normalizeType(item.type);
            if (!groups[type]) groups[type] = [];
            groups[type].push(item);
        });

        return Object.entries(groups).sort();
    }, [results]);

    const filterOptions = useMemo(
        () => [
            { key: 'all', label: 'Alle', count: results.length },
            ...groupedResults.map(([type, items]) => ({
                key: type,
                label: getTypeConfig(type).label,
                count: items.length
            }))
        ],
        [groupedResults, results.length]
    );

    useEffect(() => {
        if (activeFilter === 'all') return;
        const exists = groupedResults.some(([type]) => type === activeFilter);
        if (!exists) setActiveFilter('all');
    }, [activeFilter, groupedResults]);

    const visibleGroupedResults = useMemo(() => {
        if (activeFilter === 'all') return groupedResults;
        return groupedResults.filter(([type]) => type === activeFilter);
    }, [activeFilter, groupedResults]);

    const gridColsClass = useMemo(() => {
        const count = visibleGroupedResults.length;
        if (count === 1) return 'tw-grid-cols-1';
        if (count === 2) return 'tw-grid-cols-1 md:tw-grid-cols-2';
        if (count === 3) return 'tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3';
        return 'tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3 2xl:tw-grid-cols-4';
    }, [visibleGroupedResults.length]);

    const handlePreviewResult = (item: GlobalSearchResult) => {
        const type = normalizeType(item.type);
        const id = Number(item.id);
        if (!Number.isFinite(id)) return;

        switch (type) {
            case 'member':
                addModal({
                    title: item.title || 'Mitglied',
                    content: <ClubMemberPreview clubMemberId={id} clubSlug={clubSlug} />,
                    show: true,
                    size: 60
                });
                break;

            case 'booking':
                addModal({
                    title: item.title || 'Belegung',
                    content: <AllocationPreview allocation={{ id }} />,
                    show: true,
                    size: 70
                });
                break;

            case 'event':
                addModal({
                    title: item.title || 'Event Vorschau',
                    content: <EventPreviewContent eventId={id} />,
                    show: true,
                    size: 70
                });
                break;

            case 'invoice':
                if (!item.url) break;
                openInNewTab(item.url);
                break;

            case 'booker':
                addModal({
                    title: item.title || 'Buchende Vorschau',
                    content: <BookerPreviewContent bookerId={id} />,
                    show: true,
                    size: 70
                });
                break;

            default:
                addModal({
                    title: item.title || 'Vorschau',
                    content: (
                        <div className="tw-space-y-2 tw-p-6">
                            <div className="tw-text-sm tw-font-semibold tw-text-accents-9">
                                {item.title || 'Ohne Titel'}
                            </div>
                            {item.subtitle && (
                                <div className="tw-text-xs tw-text-accents-7">{removeTags(item.subtitle)}</div>
                            )}
                            <div className="tw-text-xs tw-text-accents-6">Typ: {getTypeConfig(type).label}</div>
                        </div>
                    ),
                    show: true,
                    size: 40
                });
        }
    };

    const handleEditResult = (item: GlobalSearchResult) => {
        const type = normalizeType(item.type);
        const id = Number(item.id);
        if (!Number.isFinite(id)) return;

        switch (type) {
            case 'event':
                addModal({
                    title: 'Termin bearbeiten',
                    content: (modalId) => <EventForm eventId={id} callback={() => removeModal(modalId)} />,
                    show: true,
                    size: 70
                });
                break;

            case 'invoice':
                addModal({
                    title: 'Rechnung bearbeiten',
                    content: (modalId) => <InvoiceForm invoice={{ id } as any} callback={() => removeModal(modalId)} />,
                    show: true,
                    size: 80
                });
                break;

            case 'member':
                addModal({
                    title: item.title || 'Mitglied',
                    content: <ClubMemberEditContent memberId={id} clubSlug={clubSlug} />,
                    show: true,
                    size: 60
                });
                break;

            case 'booking':
                addModal({
                    title: 'Buchung bearbeiten',
                    content: (modalId) => <BookingEditor bookingId={id} onClose={() => removeModal(modalId)} />,
                    show: true,
                    size: 70
                });
                break;
            default:
                addModal({
                    title: item.title || 'Suchergebnis',
                    content: (
                        <div className="tw-p-10 tw-text-center">
                            Details für diesen Typ sind aktuell nicht verfügbar.
                        </div>
                    ),
                    show: true,
                    size: 40
                });
        }
    };
    return (
        <div className="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-overflow-hidden">
            <div className="tw-relative tw-mb-2 tw-flex tw-items-center tw-px-2">
                <FontAwesomeIcon icon={faSearch} className="tw-absolute tw-left-3 tw-text-accents-4" />
                <StyledInput
                    autoFocus
                    className="tw-pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.currentTarget.value)}
                    placeholder="Suchen (z.B. Name, E-Mail...)"
                    fullWidth
                />
                {isLoading && (
                    <div className="tw-absolute tw-right-3">
                        <Spinner size="sm" />
                    </div>
                )}
            </div>
            <div className="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-overflow-y-auto">
                {query.length < 2 ? (
                    <div className="tw-my-auto tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-6 tw-text-xl tw-text-accents-8">
                        <FontAwesomeIcon icon={faArrowUp} />
                        <span>Gebe mindestens 2 Zeichen ein, um die Suche zu starten</span>
                    </div>
                ) : results.length === 0 && !isLoading ? (
                    <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-accents-5">
                        <p>Keine Ergebnisse gefunden für {query}</p>
                    </div>
                ) : (
                    <div className="tw-flex tw-h-full tw-flex-col tw-gap-2">
                        <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-px-1 tw-text-xs tw-text-accents-6">
                            <span>{results.length} Ergebniss(e)</span>
                            <span>in {visibleGroupedResults.length} Bereiche(n)</span>
                        </div>
                        <div className="tw-mb-3 tw-flex tw-flex-wrap tw-gap-2 tw-px-1">
                            {filterOptions.map((filter) => (
                                <Button
                                    key={filter.key}
                                    size="sm"
                                    color={activeFilter === filter.key ? 'success' : 'light'}
                                    onPress={() => setActiveFilter(filter.key)}
                                    className="tw-transition-all tw-duration-200"
                                >
                                    <span>{filter.label}</span>
                                    <Badge
                                        className="tw-transition-all tw-duration-200"
                                        size="xs"
                                        color={activeFilter === filter.key ? 'light' : 'success'}
                                    >
                                        {filter.count}
                                    </Badge>
                                </Button>
                            ))}
                        </div>
                        <div className={`tw-grid tw-flex-1 tw-gap-4 ${gridColsClass}`}>
                            {visibleGroupedResults.map(([type, items]) => {
                                const config = getTypeConfig(type);
                                return (
                                    <div key={type} className="tw-flex tw-flex-col tw-gap-3">
                                        <div className="tw-flex tw-items-center tw-justify-between tw-px-1">
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-success">
                                                <FontAwesomeIcon icon={config.icon} />
                                                <span className="tw-text-xs tw-font-bold tw-uppercase tw-text-accents-8">
                                                    {config.label}
                                                </span>
                                            </div>
                                            <Badge size="xs" color="success" flat>
                                                {items.length}
                                            </Badge>
                                        </div>
                                        <div className="tw-flex tw-flex-col tw-gap-1.5">
                                            {items.map((item) => {
                                                const subtitle = removeTags(item.subtitle);
                                                const canPreview = canPreviewResult(item);
                                                return (
                                                    <div
                                                        key={`${item.type}_${item.id}`}
                                                        className="tw-card-bordered tw-bg-white"
                                                    >
                                                        <div className="tw-min-w-0 tw-px-1 tw-pb-2">
                                                            <div className="tw-truncate tw-text-sm tw-font-semibold tw-text-accents-9">
                                                                {renderHighlightedText(
                                                                    item.title || 'Ohne Titel',
                                                                    query
                                                                )}
                                                            </div>
                                                            {!!subtitle && (
                                                                <div className="tw-truncate tw-text-xs tw-text-accents-8">
                                                                    {renderHighlightedText(subtitle, query)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="tw-flex tw-justify-end tw-gap-2">
                                                            {canPreview && (
                                                                <Button
                                                                    onPress={() => handlePreviewResult(item)}
                                                                    size="xs"
                                                                    color="light"
                                                                    className="tw-justify-center"
                                                                >
                                                                    Vorschau
                                                                </Button>
                                                            )}
                                                            <Button
                                                                onPress={() => handleEditResult(item)}
                                                                size="xs"
                                                                color="warning"
                                                                className="tw-justify-center"
                                                            >
                                                                Bearbeiten
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <span className="tw-mt-4 tw-flex tw-justify-center tw-text-xs tw-italic tw-text-accents-6">
                            Es werden maximal 30 Ergebnisse pro Bereich angezeigt.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};
